import type { OnCallPayInfo, OnCallServiceOrderOption } from "../api/onCallPay";

// Represents account information
export interface Account {
  description: string;
  name: string;
  altAccountCodeID: string;
  accountCodeID: number;
}

// GPS punch exception for time entries
export interface GpsException {
  punchExceptionID: number;
  timeEntryID: number;
  timeEntryHourlyID: number;
  inException: boolean;
  inExceptionMessage: string;
  outException: boolean;
  outExceptionMessage: string;
  employeeID: number;
  payPeriodID: number;
  projectID: number;
  inLatitude: number;
  inLongitude: number;
  outLatitude: number;
  outLongitude: number;
}

// Represents project-related information
export interface Project {
  name: string;
  altProjectID: string;
  projectID: number;
  latitude?: number;
  longitude?: number;
  radiusMiles?: number;
}

// Represents task information
export interface TaskInfo {
  taskInfoID: number;
  name: string;
  description: string;
}

// Represents task information with remaining and original amounts
export interface Task {
  taskInfo: TaskInfo;
  remainingAmount: number;
  originalAmount: number;
  altTaskID: string;
  projectID: number;
  taskID: number;
}

/** Payroll Timesheet Modifications (M1-B2): provenance fields present on every
 *  entity row in the admin reads. 'EMPLOYEE' | 'PAYROLL'; null on rows written
 *  before the feature (and modifiedSource stays null until a modification).
 *  Undefined = the producer (older API / fixtures) did not supply them. */
export interface EntityProvenance {
  createdSource?: string | null;
  modifiedSource?: string | null;
}

// Represents employee task data
export interface EmployeeTask extends EntityProvenance {
  task: Task;
  employeeTaskID: number;
  amountClaimed: number;
  intermediateName: string;
}

// Represents miscellaneous employee tasks
export interface MiscEmployeeTask extends EntityProvenance {
  miscEmployeeTaskID: number;
  projectID: number;
  accountCode: Account;
  amountClaimed: number;
  miscDescription: string;
  intermediateName: string;
  /** Wallcraft production totals (read-side threading, plan A7). All optional and
   *  additive: a producer that does not send them must degrade to today's UI, never
   *  to a wrong attribution. `createdSource === 'PAYROLL'` is what marks a row as a
   *  production total rather than an employee-claimed misc task. */
  createdByAdminEmployeeID?: number | null;
  /** Display name of the admin who entered the total, when the API resolves it. */
  createdByAdminName?: string | null;
  /** When the row was written (UTC ISO) + the local offset ("-07:00"). */
  insertDateTime?: string | null;
  insertDateTimeOffset?: string | null;
  /** The local day the entry belongs to (`YYYY-MM-DD`); informational — the console
   *  matches an entry to a segment on the day it is rendered under. */
  entryDate?: string | null;
}

// Represents project-related employee tasks
export interface ProjectEmployeeTasks {
  project: Project;
  employeeTasks: EmployeeTask[];
  miscEmployeeTasks: MiscEmployeeTask[];
  totalAmountClaimed: number;
}

// Represents operational task information with project and account
export interface OperationalTaskWithProjectAndAccount {
  operationalTaskID: number;
  name: string;
  projectID: number;
  accountCodeID: number;
  accountingYear: string;
  project: Project;
  accountCode: Account;
}

// Represents an employee's operational task with full nested info
export interface EmployeeOperationalTask extends EntityProvenance {
  employeeOperationalTaskID: number;
  employeeID: number;
  payPeriodID: number;
  operationalTaskID: number;
  entryDate: string;
  minutesClaimed: number;
  insertDateTime: string;
  insertDateTimeOffset: string;
  description: string;
  operationalTask: OperationalTaskWithProjectAndAccount;
  amountClaimed?: number;
  accountCode?: Account;
  originalAmount?: number;
  remainingAmount?: number;
  intermediateName?: string;
}

// Represents customer service warranty information with account
export interface CustomerServiceWarrantyWithAccount {
  customerServiceWarrantyID: number;
  name: string;
  accountCodeID: number;
  accountingYear: string;
  projectRequired: boolean;
  /** Job number the warranty bills to. Sent by the API; the claim's own project
   *  carries the same number as altProjectID when ProjectRequired is set. */
  job?: string | null;
  accountCode: Account;
}

// Represents an employee's customer service warranty claim
export interface EmployeeCustomerServiceWarranty extends EntityProvenance {
  employeeCustomerServiceWarrantyID: number;
  employeeID: number;
  payPeriodID: number;
  customerServiceWarrantyID: number;
  projectID: number;
  entryDate: string;
  minutesClaimed: number;
  amountClaimed: number;
  locationName: string;
  insertDateTime: string;
  insertDateTimeOffset: string;
  description: string;
  customerServiceWarranty: CustomerServiceWarrantyWithAccount;
  intermediateName?: string;
  accountCode?: Account;
  project?: Project;
}

// Represents a work order task with its rate details
export interface WorkOrderTaskDetails {
  workOrderTaskID: number;
  name: string;
  description: string | null;
  laborPerHour: number;
  totalHours: number;
  remainingHours: number;
  accountCodeID: number;
  altAccountCodeID: string | null;
}

// Represents an employee's work order hour claim (hours-flavored twin of EmployeeTask)
export interface EmployeeWorkOrderTask extends EntityProvenance {
  employeeWorkOrderTaskID: number;
  employeeID: number;
  payPeriodID: number;
  entryDate: string;
  hoursClaimed: number;
  hourlyRate?: number; // rate snapshotted on the claim (TAS-291); falls back to workOrderTask.laborPerHour
  rateSource?: 'override' | 'system' | 'legacy';
  amountImplied: number;
  insertDateTime: string;
  insertDateTimeOffset: string;
  workOrderTask: WorkOrderTaskDetails;
  workOrder: {
    workOrderID: number;
    projectID: number;
  };
  project: {
    projectID: number;
    altProjectID: string;
    name: string;
  };
}

// Represents the available sick time for an employee
export interface EmployeeAvailableSickAmount {
  employeeAvailableSickAmountID: number;
  employeeID: number;
  availableSickMinutes: number;
}

// Represents an employee's sick leave claim
export interface EmployeeSickClaim extends EntityProvenance {
  employeeSickClaimID: number;
  employeeID: number;
  payPeriodID: number;
  entryDate: string;
  minutesClaimed: number;
  amountClaimed: number;
  insertDateTime: string;
  insertDateTimeOffset: string;
  description: string;
  accountCode?: Account;
  intermediateName?: string;
  // Hourly rate entered by payroll (0 until entered). Drives the sick-rate panel's
  // saved-rate display and the "still needs a rate" approve checks.
  hourlyAmount?: number;
  // Set for sick claims materialized from an approved sick Time Off Request (2020PH).
  // Null/undefined for WCDP employee-filed claims.
  timeOffRequestID?: number | null;
}

// Service order info joined onto a time entry
export interface ServiceOrderInfo {
  /** Sent by the timesheetEdit/state read; older producers omit it. */
  serviceOrderID?: number;
  altServiceOrderID: string;
  name: string | null;
  projectID: number | null;
  isActive?: boolean;
}

/** TAS-504/507 — the ServiceOrderScope hydrated onto a resolved NPM service-order punch.
 *  Sent only by the NPM tenant (`TimeEntryHourly.serviceOrderScope` on the API); every
 *  other tenant sends null/undefined and every consumer must render exactly as before. */
export interface ServiceOrderScopeInfo {
  serviceOrderScopeID: number;
  scopeSeq: number | null;
  description: string | null;
  division: string | null;
  /** JC job on a backcharge (type J) service order; null on ordinary service work. */
  job: string | null;
}

// Represents hourly time entries for an employee
export interface TimeEntryHourly extends EntityProvenance {
  timeEntryHourlyID: number;
  employeeID: number;
  payPeriodID: number;
  inDateTime: string;
  inDateTimeOffset: string;
  outDateTime: string;
  outDateTimeOffset: string;
  type: number;
  punchTypeIn: number;
  punchTypeOut: number;
  projectID: number;
  accountCodeID: number;
  inLatitude?: number;
  inLongitude?: number;
  outLatitude?: number;
  outLongitude?: number;
  gpsException?: GpsException;
  serviceOrderID?: number | null;
  tempServiceOrderDescription?: string | null;
  serviceOrder?: ServiceOrderInfo | null;
  /** TAS-504 punch-level ServiceOrderScope FK; null outside NPM service-order work. */
  serviceOrderScopeID?: number | null;
  /** TAS-507 hydrated scope behind `serviceOrderScopeID` (no extra request). NPM only. */
  serviceOrderScope?: ServiceOrderScopeInfo | null;
  driveTimeEntryID?: number | null;
  /** Free-text service-order / PO number on the punch. Wallcraft production totals
   *  reuse it for the Purchase Order account code's PO # (locked decision 1); it also
   *  rides on the hourly GROUP (`HourlyProject.serviceOrderText`), which is where the
   *  console reads it from first. Absent on producers that do not send it. */
  serviceOrderText?: string | null;
  /** Overnight on-call shift (2020CA/NV, AllowOnCallPay roles only): this punch ends at
   *  12:00 AM and the shift continues on the next day. Sent by getEmployeeTimesheet,
   *  getUnsubmittedEmployeeTimesheet and timesheetEdit/state (exists:false). */
  overnightOut?: boolean;
  /** Overnight on-call shift: this punch starts at 12:00 AM, continuing from the previous day. */
  overnightIn?: boolean;
}

/** Project-phase block on an hourly group (payroll editor). `projectPhaseID` is
 *  typed number but is null at runtime for bare (no-job) groups. */
export interface HourlyProjectPhase {
  projectPhaseID: number;
  altProjectPhaseID: string;
  name: string;
  description: string;
}

// Represents hourly project information with time entries.
// NOTE (payroll editor, M2-B): bare (non-job-costed) punches form a group whose
// `project` fields are ALL null at runtime — detect with `project.projectID == null`,
// never by string-matching the name (API brief §2). The optional fields below ride
// on the timesheetEdit/state read; older producers omit them.
export interface HourlyProject {
  project: Project;
  account: Account;
  timeEntriesHourly: TimeEntryHourly[];
  projectPhase?: HourlyProjectPhase | null;
  serviceOrderText?: string | null;
  serviceOrderAltID?: string | null;
  serviceOrderID?: number | null;
  /** TAS-507 — the group's ServiceOrderScope, flattened by the API onto
   *  `HourlyProjectGroup` (src/db/timesheetsUtil.ts). NPM only; null everywhere else,
   *  so every existing consumer keeps behaving exactly as it did. */
  serviceOrderScopeID?: number | null;
  scopeSeq?: number | null;
  scopeDescription?: string | null;
  scopeDivision?: string | null;
  scopeJob?: string | null;
  /** TAS-507 — an unresolved (temporary) NPM service order typed by the employee. */
  tempServiceOrderDescription?: string | null;
  customerServiceWarranty?: {
    customerServiceWarrantyID: number | null;
    name: string | null;
    accountCodeID: number | null;
    projectRequired?: boolean;
    accountingYear?: string | null;
    job?: string | null;
  } | null;
  locationName?: string | null;
  description?: string | null;
}

// Represents time entries for employees
export interface TimeEntry extends EntityProvenance {
  timeEntryID: number;
  employeeID: number;
  payPeriodID: number;
  inDateTime: string;
  inDateTimeOffset: string;
  outDateTime: string;
  outDateTimeOffset: string;
  type: number;
  punchTypeIn: number;
  punchTypeOut: number;
  inLatitude?: number;
  inLongitude?: number;
  outLatitude?: number;
  outLongitude?: number;
  gpsException?: GpsException;
}

// Represents a day of work for an employee
export interface Day {
  date: string;
  regularTime: number;
  overTime: number;
  doubleTime: number;
  totalWorkMinutes: number;
  totalTaskMinutes?: number;
  totalHourlyMinutes?: number;
  totalClaimedAmount: number;
  timeEntries: TimeEntry[];
  hourlyProjects: HourlyProject[];
  projectEmployeeTasks: ProjectEmployeeTasks[];
  employeeCustomerServiceWarrantyTasks: EmployeeCustomerServiceWarranty[];
  employeeSickClaims: EmployeeSickClaim[];
  employeeOperationalTasks: EmployeeOperationalTask[];
  employeeWorkOrderTasks: EmployeeWorkOrderTask[];
  totalWorkOrderHours?: number;
  totalWorkOrderAmount?: number;
  fiveHourLunchException: boolean;
  fullLunchException: boolean;
  noLunchException: boolean;
  minWageTrueUp?: {
    needed: boolean;
    pieceHours: number;
    pieceEarnings: number;
    pieceRestMinutes?: number;
    minimumWageRate: number;
    minimumWageFloor: number;
    trueUpAmount: number;
    stateAbbr: string;
  };
}

// Represents employee roles and permissions
export interface Role {
  roleID: number;
  name: string;
  admin: boolean;
  supervisor: boolean;
  stateAbbr: string;
  recordHours: boolean;
  recordHoursJob: boolean;
  recordHoursJobAcctCodeReq: boolean;
  recordTasks: boolean;
  recordPhoto: boolean;
  recordLocation: boolean;
  recordHoursOther?: boolean;
  recordHoursCustomerServiceText?: boolean;
  recordHoursCustomerServiceAndWarranty?: boolean;
  recordHoursServiceOrder?: boolean;
  /** Wallcraft production totals gate (TAS-410+). See RoleInfo in EmployeeInfoInterface.ts. */
  requireProductionPay?: boolean;
  /** 2020CA/2020NV On Call $ eligibility. See RoleInfo in EmployeeInfoInterface.ts. */
  allowOnCallPay?: boolean;
  /** Per-Role mobile Clock/Hourly V2 switch (NPM). See RoleInfo in EmployeeInfoInterface.ts. */
  useClockHourlyV2?: boolean;
  /** Per-Role mobile Time Off Requests switch (NPM). See RoleInfo in EmployeeInfoInterface.ts. */
  allowTimeOffRequests?: boolean;
  active: boolean;
  accountCodes: number[];
  locations: number[];
}

// Represents employee name data
export interface EmployeeName {
  last: string;
  middle: string;
  first: string;
}

/** TAS-447 — the API resolves a required approver off the Employee hierarchy, which
 *  does not guarantee they hold an AdminEmployee record (a login). `hasApproverLogin`
 *  is false for those: the slot is real and blocks the timesheet, but its owner can
 *  never clear it, so only an admin / approveTimesheets holder can act on their behalf.
 *  Optional because older API builds do not send it — treat a missing value as "fine". */
interface RequiredApproverFields {
  hasApproverLogin?: boolean;
}

// Represents employee information with work data
export interface ManagerApproval {
  requiredEmployee?: Employee & RequiredApproverFields & {
    requiredManagersApprovalsID: number;
  };
  approvalEmployee?: Employee & {
    approvalDateTime: string;
    approvalDateTimeOffset: string;
  };
}

export interface SuperApproval {
  requiredEmployee?: Employee & RequiredApproverFields & {
    requiredSuperApprovalsID: number;
  };
  approvalEmployee?: Employee & {
    approvalDateTime: string;
    approvalDateTimeOffset: string;
  };
}

/**
 * TAS-679 (WCDP Modified Duty) — one required supervisor approval slot, flattened by the
 * API from `superApprovals`. Sent ONLY when the timesheet carries 2+ slots (the employee was
 * on Modified Duty during the week, so HR approves in addition to the supervisor). Every
 * other tenant / timesheet omits the field entirely.
 */
export interface RequiredSuperSlot {
  requiredSuperApprovalsID: number;
  superEmployeeID: number;
  name: string;
  approved: boolean;
  approvedBy: string | null;
  approvalDateTime: string | null;
  /** True for the Modified Duty (HR) approver's slot. */
  isModifiedDutyApprover: boolean;
}

export interface PayrollApproval {
  approvalEmployee?: Employee & {
    approvalDateTime: string;
    approvalDateTimeOffset: string;
  };
}

export interface TimesheetApprovalEntry {
  approvalEmployee: {
    altEmployeeID: string | null;
    email: string;
    active: boolean;
    name: { first: string; middle: string | null; last: string; };
  };
  approvalDateTime: string;
  approvalDateTimeOffset: string;
}

export interface TimesheetQuestion {
  question: string;
  question_es: string;
  answerType: number;
  /** 'Yes' | 'No' | null. Null (or absent on older API builds) = indifferent:
   *  the answer is displayed neutrally and never flagged. */
  expectedAnswer?: string | null;
}

export interface TimesheetAnswerEntry {
  timesheetAnswerID: number;
  timesheetID: number;
  timesheetQuestionID: number;
  answer: string;
  timesheetQuestion: TimesheetQuestion;
}

// Represents an approval group with a set of admin employees who can approve
export interface ApprovalGroup {
  approvalGroupID: number;
  name: string;
  adminEmployees: Array<Employee & { adminEmployeeID: number }>;
  requiredApprovalGroupsID?: number;
  approvalEmployee?: Employee & {
    approvalDateTime: string;
    approvalDateTimeOffset: string;
  };
}

export interface Employee {
  altEmployeeID: string;
  employeeID: number;
  role: Role;
  jobTitle: string;
  hourlyRate: number;
  name: EmployeeName;
  phoneNumber: string;
  timesheetID: number;
  days: Day[];
  managerApprovals?: ManagerApproval[];
  superApprovals?: SuperApproval[];
  /** TAS-679 — present only with 2+ required supervisor slots (WCDP Modified Duty). */
  requiredSupers?: RequiredSuperSlot[];
  payrollApprovals?: PayrollApproval[];
  approvalGroups?: ApprovalGroup[];
  supervisorApprovals?: TimesheetApprovalEntry[];
  timesheetAnswers?: TimesheetAnswerEntry[];
  /** Payroll Timesheet Modifications (M1-B2) — additive fields from the admin
   *  reads. Undefined means "this producer did not supply it", never false. */
  /** False = terminated/inactive employee (with pending time when unsubmitted). */
  active?: boolean;
  /** 'EMPLOYEE' | 'PAYROLL'. PAYROLL drives the console's provenance header and
   *  the PDF's "Unavailable to sign" stamp (rendered server-side). */
  signatureType?: string;
  /** Full name of the admin who submitted on the employee's behalf; null otherwise. */
  submittedByAdminName?: string | null;
  /** Submission instant (UTC ISO) + the employee's true local offset ("-07:00").
   *  Selected by every submitted-timesheet read; null/absent until submitted. */
  submittedDate?: string | null;
  submittedDateOffset?: string | null;
  /** True once any payroll edit has touched this timesheet (M3 sets it; M1 reads it). */
  payrollModified?: boolean;
  lastPayrollModifiedAt?: string | null;
  /** 2020CA/2020NV weekly On Call $ (TAS-475). Absent on other tenants and on the
   *  unsubmitted read; `amount: null` = nothing entered for the week. */
  onCallPay?: OnCallPayInfo;
  /** The week's service orders the On Call $ can be charged to (most recent first). Present
   *  alongside `onCallPay`; absent/empty → only "No service order". */
  onCallServiceOrderOptions?: OnCallServiceOrderOption[];
}

// Interface for employee list page props
export interface EmployeeListPageProps {
  setError: (error: string | null) => void;
}

// Interface for the employee list response
export interface EmployeeListResponse {
  data: Employee[];
}

// Enum for employee search timesheet status
export enum EmployeeSearchTimesheetStatus {
  Unsubmitted = "Unsubmitted",
  FirstLevelApproval = "1stLevelApproval",
  SecondLevelApproval = "2ndLevelApproval",
  PayrollPending = "PayrollPending",
  PayrollApproved = "PayrollApproved",
  NoTimesheetForPayPeriod = "NoTimesheetForPayPeriod"
}

// Interface for employee search result
export interface EmployeeSearchResult {
  altEmployeeID: string;
  employeeID: number; // Employee ID for navigation to timesheet
  name: {
    last: string;
    middle: string;
    first: string;
  };
  location: {
    name: string;
    altLocationID: string;
    locationID: number;
  };
  timesheetStatus: EmployeeSearchTimesheetStatus;
}

export const defaultEmployee: Employee = {
  altEmployeeID: '',
  employeeID: 0,
  role: {
    roleID: 0,
    name: '',
    admin: false,
    supervisor: false,
    stateAbbr: '',
    recordHours: false,
    recordHoursJob: false,
    recordHoursJobAcctCodeReq: false,
    recordTasks: false,
    recordPhoto: false,
    recordLocation: false,
    active: false,
    accountCodes: [],
    locations: [],
  },
  jobTitle: '',
  hourlyRate: 0,
  name: {
    last: '',
    middle: '',
    first: '',
  },
  phoneNumber: '',
  timesheetID: 0,
  days: [],
};
